import { useEffect, useState } from 'react';
import { Input, Switch, Button, Badge, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { X } from 'lucide-react';
import { SectionCard, QueryState, StatusBadge } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { parsePins } from '../../lib/settings';


/** SELL-08 Locations & hours + doorstep-pickup PIN codes (real since D7; the PINs gate repair pickups). */
export default function LocationsTab() {
  const { toast } = useToast();
  const q = useQuery('merchantLocations', {}, { select: (r) => r.data });
  const save = useMutation('merchantLocationsSave');
  const [draft, setDraft] = useState(null);
  const [pinText, setPinText] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (q.data) setDraft(JSON.parse(JSON.stringify(q.data)));
  }, [q.data]);

  const setHours = (li, di, patch) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    Object.assign(next.locations[li].hours[di], patch);
    return next;
  });
  const addPins = () => {
    const { valid, invalid } = parsePins(pinText);
    setPinError(invalid.length ? `Not a valid PIN: ${invalid.join(', ')}` : '');
    if (valid.length) setDraft((d) => ({ ...d, pins: [...new Set([...d.pins, ...valid])].sort() }));
    if (!invalid.length) setPinText('');
  };
  const submit = async () => {
    try {
      await save.run(draft);
      toast.success('Locations saved');
      q.reload();
    } catch { /* save.error */ }
  };

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {() => draft ? (
          <>
            <div className="grid gap-4 xl:grid-cols-2">
              {draft.locations.map((loc, li) => (
                <SectionCard key={loc.id} title={loc.name} actions={<StatusBadge status="active" label={loc.type} />}>
                  <p className="text-sm opacity-80">{loc.address}, {loc.city} {loc.pin} · {loc.phone}</p>
                  <div className="space-y-2">
                    {loc.hours.map((h, di) => (
                      <div key={h.day} className="grid grid-cols-[3rem_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2">
                        <span className="text-sm font-medium">{h.day}</span>
                        <Input aria-label={`${loc.name} ${h.day} opens`} value={h.open} disabled={h.closed} onChange={(e) => setHours(li, di, { open: e.target.value })} />
                        <Input aria-label={`${loc.name} ${h.day} closes`} value={h.close} disabled={h.closed} onChange={(e) => setHours(li, di, { close: e.target.value })} />
                        <Switch size="sm" checked={!h.closed} onChange={(open) => setHours(li, di, { closed: !open })} ariaLabel={`${loc.name} open on ${h.day}`} />
                      </div>
                    ))}
                  </div>
                </SectionCard>
              ))}
            </div>

            <SectionCard title={`Doorstep pickup PIN codes (${draft.pins.length})`}>
              <p className="text-sm opacity-70">Repair pickups are offered only in these PIN codes (others can drop off at a store). Delivery coverage at checkout follows the platform's courier zones.</p>
              <div className="flex flex-wrap gap-2">
                {draft.pins.map((p) => (
                  <Badge key={p} variant="outline">
                    <span className="inline-flex items-center gap-1">
                      {p}
                      <button type="button" aria-label={`Remove ${p}`} className="opacity-60 hover:opacity-100" onClick={() => setDraft((d) => ({ ...d, pins: d.pins.filter((x) => x !== p) }))}><X size={12} /></button>
                    </span>
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap items-start gap-2">
                <div className="flex-1 min-w-[14rem]"><Input placeholder="Add PIN codes, e.g. 560001, 560017" value={pinText} onChange={(e) => setPinText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPins()} error={pinError} aria-label="Add PIN codes" /></div>
                <Button variant="outline" onClick={addPins} disabled={!pinText.trim()}>Add</Button>
              </div>
            </SectionCard>
            {save.error ? <Alert variant="error">{save.error}</Alert> : null}
            <Button onClick={submit} loading={save.loading}>Save locations & coverage</Button>
          </>
        ) : null}
      </QueryState>
    </div>
  );
}
